import type { Contract, Milestone } from '../../types';
import { agreementRoot, agreementVariationNumber } from '../../utils/agreementIdentity';
import { combinedAgreementTimeline } from '../../utils/combinedAgreementTimeline';
import { isMilestoneSettledForClosure, isReplacedMilestone } from '../../utils/milestoneHistory';

export type AgreementChoice = 'fixed' | 'variation';

/** What an ÄTA from "Vad vill du skapa?" builds on (ClickUp 869fh4c2b). */
export interface VariationBasis {
  /** The agreement in force; the ÄTA wizard is opened from it. */
  parentId: string;
  /** Number of the original contract the chain started from. */
  contractNumber: number;
  /** The latest signed ÄTA included, or null when there is none yet. */
  variationNumber: number | null;
  afterRotKronor: number;
  stageCount: number;
  completedCount: number;
}

const isSignedAndCurrent = (contract: Contract) =>
  contract.draftStatus === 'signed_by_both' && (contract.versionStatus ?? 'current') === 'current';

/** Whether any contract in the project has been signed by both parties. */
export const hasSignedAgreement = (contracts: Contract[]) =>
  contracts.some(contract => contract.draftStatus === 'signed_by_both');

/**
 * The version in force for a contract, resolved as the ÄTA wizard does: a
 * price-adjustment ÄTA belongs to its contract, and a replaced version is
 * followed to the version that replaced it.
 */
export function agreementInForce(contract: Contract, contracts: Contract[]): Contract {
  let current = contract;
  if (current.settlementType === 'price_adjustment' && current.parentContractId) {
    current = contracts.find(candidate => candidate.id === current.parentContractId) ?? current;
  }
  const visited = new Set<string>();
  while (current.supersededByContractId && !visited.has(current.id)) {
    visited.add(current.id);
    const successorId = current.supersededByContractId;
    const successor = contracts.find(candidate => candidate.id === successorId);
    if (!successor) break;
    current = successor;
  }
  return current;
}

/**
 * The signed agreement an ÄTA builds on: the original contract plus every ÄTA
 * in force. The chain of the selected contract is preferred; otherwise the
 * chain of the lowest contract number. Null when no ÄTA can be created.
 * Stages are counted from the same combined timeline as the project view.
 */
export function variationBasis(
  contracts: Contract[],
  stages: Milestone[],
  preferredContractId?: string | null
): VariationBasis | null {
  const candidates = contracts
    .filter(isSignedAndCurrent)
    .map(contract => agreementInForce(contract, contracts))
    .filter(isSignedAndCurrent);
  if (candidates.length === 0) return null;

  const preferred = contracts.find(contract => contract.id === preferredContractId);
  const preferredInForce = preferred && agreementInForce(preferred, contracts);
  const rootNumber = (contract: Contract) => agreementRoot(contract, contracts).contractNumber;
  const current = candidates.find(candidate => candidate.id === preferredInForce?.id) ??
    [...candidates].sort((left, right) => rootNumber(left) - rootNumber(right))[0];

  const timeline = combinedAgreementTimeline(current, contracts, stages);
  const items = (timeline?.milestones ?? stages.filter(stage => stage.contractId === current.id))
    .filter(stage => !isReplacedMilestone(stage) && stage.itemType !== 'variation_price_adjustment');

  return {
    parentId: current.id,
    contractNumber: rootNumber(current),
    variationNumber: agreementVariationNumber(current),
    afterRotKronor: Number(current.afterRotAmount) || 0,
    stageCount: items.length,
    completedCount: items.filter(isMilestoneSettledForClosure).length
  };
}
